import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qcpSocketAuth } from '@/lib/qcp/auth';
import {
  ClientEvents,
  ServerEvents,
  type Board,
  type Card,
  type ClockPayload,
  type DealPayload,
  type EndedPayload,
  type ErrorPayload,
  type HandSettledPayload,
  type HandStartPayload,
  type MatchResultPayload,
  type PlaceFlSubmit,
  type PlaceSubmit,
  type ProjectedState,
  type RevealPayload,
  type Row,
  type Seat,
} from '@/lib/qcp/protocol';

/** Default per-seat zero record (A/B) — used to seed the running-net & FL maps. */
const ZERO_AB: Record<Seat, number> = { A: 0, B: 0 };
const FALSE_AB: Record<Seat, boolean> = { A: false, B: false };

export interface QcpMatchSocket {
  connected: boolean;
  /** Full projected game state — null until the first match:state arrives. */
  state: ProjectedState | null;
  /** Cards dealt to this player for the current hand (deck:commit → deal:cards). */
  draw: Card[];
  /** Per-seat banked clock times + whose turn the clock is tracking. */
  clocks: ClockPayload | null;
  /**
   * The server's commit hash for the current deck (used for provably-fair
   * verification). Set on deck:commit and cleared between hands.
   */
  commitHash: string | null;
  /** The deck-reveal payload emitted at end of hand (provably-fair audit). */
  reveal: RevealPayload | null;
  /**
   * Legacy single-hand terminal payload — set when match:ended fires. The
   * match-over trigger has MOVED to `matchResult` (the multi-hand set end); this
   * field is retained because other code (ChinesePokerMatch) still reads it to
   * navigate to the result screen and to gate the resign button. In the
   * multi-hand loop the server may still emit match:ended for the final hand.
   */
  ended: EndedPayload | null;
  /** Transient server error — auto-cleared after 4 s. */
  error: ErrorPayload | null;
  /**
   * This client's seat identity, derived from the first match:state projection:
   * `mySeat` is the seat key NOT present in `state.opponents` (the projection
   * only includes the *opponent's* masked entry — see project.ts). Null until
   * the first state arrives.
   */
  mySeat: Seat | null;

  /* ── Multi-hand "set" loop state ─────────────────────────────────────── */

  /** 1-based index of the current hand within the set (from hand:start). */
  handNumber: number;
  /** Cumulative net score across the set so far (from hand:settled running net). */
  runningNet: Record<Seat, number>;
  /** Per-seat Fantasyland flag for the CURRENT hand (from hand:start). */
  inFantasyland: Record<Seat, boolean>;
  /** Per-seat Fantasyland card count for the CURRENT hand (0 = normal seat). */
  flCardCount: Record<Seat, number>;
  /**
   * The terminal match-end signal — set ONCE when match:result fires. This is
   * the canonical "the set is over" trigger for the multi-hand loop (it carries
   * final net, winner, hands played, reason and ELO deltas).
   */
  matchResult: MatchResultPayload | null;
  /**
   * The most recent hand:settled payload — exposes the last reveal + per-hand
   * result for an optional inter-hand summary screen. Null until the first hand
   * settles.
   */
  handSettled: HandSettledPayload | null;

  /**
   * Emit deck:clientSeed. Normally called automatically by the hook once per
   * hand (on deck:commit / hand:start, provably-fair handshake). Exposed so the
   * UI can trigger a manual re-send if needed (rare edge-case; the hook guards
   * against a second automatic emit within the same hand but allows an explicit
   * call).
   */
  submitSeed: (seed: string) => void;
  /** Emit place:submit with this client's placement + optional discard. */
  place: (placements: PlaceSubmit['placements'], discard: Card | null) => void;
  /**
   * Emit place:fl — a Fantasyland seat sets its full 13-card board (front 3 /
   * middle 5 / back 5) in a single move, with the leftover FL cards discarded.
   */
  placeFl: (board: Board, discards: Card[]) => void;
  /** Emit match:resign. */
  resign: () => void;
}

/**
 * Connects to the qcp-gameserver (Socket.IO, :4101) for one Chinese Poker
 * match (a multi-hand "set"). Auth on the handshake comes from `qcpSocketAuth()`:
 * a per-user Logto access token (`auth.token`) when signed in, else the
 * dev-bypass fallback (`auth.devBypass`/`auth.devUser`, dev only).
 *
 * The hook registers listeners for every server event defined in ServerEvents
 * and derives the client's seat identity from the first match:state projection
 * (the seat key absent from `state.opponents` is this client's own seat).
 *
 * The provably-fair deck handshake (deck:commit → deck:clientSeed) fires
 * exactly ONCE PER HAND: a `seedSentRef` flag prevents a re-emit on reconnect
 * within a hand, and is RESET at the start of each hand (on hand:start, and
 * defensively on deck:commit) so the next hand performs a fresh handshake. The
 * client seed is generated using `crypto.getRandomValues` and returned as a
 * 32-byte hex string.
 *
 * Anti-leak: this hook never reads `state.opponents[seat].draw` or
 * `state.opponents[seat].discards` — those fields do not exist on the
 * `ProjectedState.opponents` type (only `drawCount`/`discardCount` are
 * present by design). For a Fantasyland opponent the UI shows only a status +
 * count; the opponent's private FL cards are never emitted by the server.
 */
export function useQcpMatch(
  matchId: string | null,
  opts?: { vsBot?: boolean },
): QcpMatchSocket {
  const socketRef = useRef<Socket | null>(null);
  /**
   * Guards the client-seed auto-emit FOR THE CURRENT HAND: once the seed has
   * been sent for this hand (either by the auto-handshake on deck:commit /
   * hand:start, or by an explicit submitSeed call from the UI), we must NOT send
   * another one — the server expects exactly one seed per seat per HAND. Reset
   * to false at the start of every hand (hand:start / deck:commit) and on a new
   * matchId.
   */
  const seedSentRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<ProjectedState | null>(null);
  const [draw, setDraw] = useState<Card[]>([]);
  const [clocks, setClocks] = useState<ClockPayload | null>(null);
  const [commitHash, setCommitHash] = useState<string | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [ended, setEnded] = useState<EndedPayload | null>(null);
  const [error, setError] = useState<ErrorPayload | null>(null);
  const [mySeat, setMySeat] = useState<Seat | null>(null);

  // Multi-hand "set" loop state.
  const [handNumber, setHandNumber] = useState(0);
  const [runningNet, setRunningNet] = useState<Record<Seat, number>>(ZERO_AB);
  const [inFantasyland, setInFantasyland] = useState<Record<Seat, boolean>>(FALSE_AB);
  const [flCardCount, setFlCardCount] = useState<Record<Seat, number>>(ZERO_AB);
  const [matchResult, setMatchResult] = useState<MatchResultPayload | null>(null);
  const [handSettled, setHandSettled] = useState<HandSettledPayload | null>(null);

  // Capture opts.vsBot in a ref so the effect closure is stable across
  // re-renders (opts is a new object on every render otherwise).
  const vsBotRef = useRef(opts?.vsBot);
  vsBotRef.current = opts?.vsBot;

  useEffect(() => {
    if (!matchId) return;

    // Reset per-match state so the hook is clean for a new matchId.
    seedSentRef.current = false;
    setConnected(false);
    setState(null);
    setDraw([]);
    setClocks(null);
    setCommitHash(null);
    setReveal(null);
    setEnded(null);
    setError(null);
    setMySeat(null);
    setHandNumber(0);
    setRunningNet(ZERO_AB);
    setInFantasyland(FALSE_AB);
    setFlCardCount(ZERO_AB);
    setMatchResult(null);
    setHandSettled(null);

    // `cancelled` guards against the async auth resolution completing AFTER
    // the cleanup function runs (e.g. strict-mode double-mount). Without the
    // guard we would connect a socket the cleanup can no longer reach.
    let cancelled = false;

    void qcpSocketAuth().then((auth) => {
      if (cancelled) return;

      const socket = io(env.qcpWsUrl, {
        auth,
        transports: ['websocket', 'polling'],
        reconnection: true,
      });
      socketRef.current = socket;

      // ------------------------------------------------------------------
      // Lifecycle
      // ------------------------------------------------------------------

      socket.on('connect', () => {
        setConnected(true);
        socket.emit(ClientEvents.Join, { matchId, vsBot: vsBotRef.current });
      });

      socket.on('disconnect', () => {
        setConnected(false);
      });

      // ------------------------------------------------------------------
      // Server events → React state
      // ------------------------------------------------------------------

      socket.on(ServerEvents.State, (p: ProjectedState) => {
        setState(p);

        // Derive mySeat once: the opponents map contains the OTHER seat's
        // masked entry. The key NOT in opponents is this client's seat.
        // Since seats are only 'A' | 'B', if opponents has 'B' → mySeat='A',
        // if opponents has 'A' → mySeat='B'.
        setMySeat((cur) => {
          if (cur !== null) return cur; // Already known — don't re-derive.
          const opponentKeys = Object.keys(p.opponents) as Seat[];
          if (opponentKeys.length === 0) return null; // Not enough info yet.
          const [oppSeat] = opponentKeys;
          return oppSeat === 'A' ? 'B' : 'A';
        });
      });

      // ------------------------------------------------------------------
      // Multi-hand: a new hand begins. Reset the per-hand handshake guard and
      // the per-hand draw, and adopt the hand's Fantasyland state. The fresh
      // seed handshake then fires on the upcoming deck:commit for this hand.
      // ------------------------------------------------------------------
      socket.on(ServerEvents.HandStart, (p: HandStartPayload) => {
        seedSentRef.current = false;
        setDraw([]);
        setReveal(null);
        setCommitHash(null);
        setHandNumber(p.handNumber);
        setInFantasyland(p.inFantasyland);
        setFlCardCount(p.flCardCount);
      });

      socket.on(ServerEvents.Commit, ({ commitHash: hash }: { commitHash: string }) => {
        setCommitHash(hash);

        // Auto-handshake: generate a random client seed and send it once PER
        // HAND. The server uses this to build the full commit (serverSeed xor
        // clientSeed → provably fair). Sending a second seed within the same
        // hand would be rejected, so seedSentRef guards this path (it is reset
        // on each hand:start). deck:commit also resets it defensively in case
        // the commit arrives before/without a hand:start (e.g. hand 1 opener).
        if (!seedSentRef.current) {
          seedSentRef.current = true;
          const bytes = new Uint8Array(32);
          crypto.getRandomValues(bytes);
          const seed = Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
          socket.emit(ClientEvents.ClientSeed, { matchId, seed });
        }
      });

      socket.on(ServerEvents.Deal, (p: DealPayload) => {
        setDraw(p.cards);
      });

      // PlaceApplied — acknowledgement / animation trigger. Registered as a
      // no-op here so the listener slot is attached and can be cleaned up with
      // the socket on disconnect. The full board state arrives via the
      // follow-up match:state; animation hooks in Task 5+ can subscribe to
      // ServerEvents.PlaceApplied on the same socket independently.
      socket.on(ServerEvents.PlaceApplied, () => {
        // no-op — board state updated via match:state
      });

      socket.on(ServerEvents.Reveal, (p: RevealPayload) => {
        setReveal(p);
      });

      socket.on(ServerEvents.Ended, (p: EndedPayload) => {
        setEnded(p);
      });

      // ------------------------------------------------------------------
      // Multi-hand: a hand has settled. Update the cumulative running net and
      // expose the last reveal + per-hand result for an optional inter-hand
      // summary. The match is NOT over here (that's match:result).
      // ------------------------------------------------------------------
      socket.on(ServerEvents.HandSettled, (p: HandSettledPayload) => {
        setHandSettled(p);
        setRunningNet({ A: p.runningNetA, B: p.runningNetB });
        if (p.reveal) {
          setReveal({
            serverSeed: p.reveal.serverSeed,
            deckOrder: p.reveal.deckOrder,
            discards: p.reveal.discards,
          });
        }
      });

      // ------------------------------------------------------------------
      // Multi-hand: the set is over (emitted once). This is the canonical
      // match-end trigger for the multi-hand loop.
      // ------------------------------------------------------------------
      socket.on(ServerEvents.MatchResult, (p: MatchResultPayload) => {
        setMatchResult(p);
      });

      socket.on(ServerEvents.Clock, (p: ClockPayload) => {
        setClocks(p);
      });

      socket.on(ServerEvents.Err, (p: ErrorPayload) => {
        setError(p);
        // Auto-clear the transient error after 4 s (mirrors useBackgammonSocket).
        window.setTimeout(
          () => setError((cur) => (cur === p ? null : cur)),
          4000,
        );
      });
    });

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [matchId]);

  // ------------------------------------------------------------------
  // Stable emit helper
  // ------------------------------------------------------------------

  const emit = useCallback((event: string, payload: unknown) => {
    socketRef.current?.emit(event, payload);
  }, []);

  // ------------------------------------------------------------------
  // Public imperative API
  // ------------------------------------------------------------------

  return {
    connected,
    state,
    draw,
    clocks,
    commitHash,
    reveal,
    ended,
    error,
    mySeat,
    handNumber,
    runningNet,
    inFantasyland,
    flCardCount,
    matchResult,
    handSettled,

    submitSeed: useCallback(
      (seed: string) => {
        // Enforce the seed-once-per-hand invariant at the hook level: the server
        // accepts exactly one client seed per seat per HAND — a second emission
        // within the same hand is rejected. The auto-handshake (on deck:commit)
        // normally fires this; a manual call is only a no-op safety net within
        // the hand and must not double-emit. The guard is reset on hand:start.
        if (seedSentRef.current) return;
        seedSentRef.current = true;
        emit(ClientEvents.ClientSeed, { matchId, seed });
      },
      [emit, matchId],
    ),

    place: useCallback(
      (placements: { card: Card; row: Row }[], discard: Card | null) => {
        if (!matchId) return;
        emit(ClientEvents.Place, { matchId, placements, discard } satisfies PlaceSubmit);
      },
      [emit, matchId],
    ),

    placeFl: useCallback(
      (board: Board, discards: Card[]) => {
        if (!matchId) return;
        emit(ClientEvents.PlaceFl, {
          matchId,
          placements: board,
          discards,
        } satisfies PlaceFlSubmit);
      },
      [emit, matchId],
    ),

    resign: useCallback(() => {
      emit(ClientEvents.Resign, { matchId });
    }, [emit, matchId]),
  };
}
